@file:OptIn(ExperimentalAtomicApi::class)

package org.maplibre.compose.map

import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.referentialEqualityPolicy
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.saveable.Saver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshots.Snapshot
import androidx.compose.runtime.structuralEqualityPolicy
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.DpRect
import androidx.compose.ui.unit.dp
import kotlin.concurrent.atomics.AtomicReference
import kotlin.concurrent.atomics.ExperimentalAtomicApi
import kotlin.jvm.JvmInline
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlinx.atomicfu.locks.reentrantLock
import kotlinx.atomicfu.locks.withLock
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.CoroutineStart
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.cancelAndJoin
import kotlinx.coroutines.channels.BufferOverflow
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.launch
import kotlinx.coroutines.selects.select
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import org.maplibre.compose.camera.CameraMoveReason
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.camera.Viewport
import org.maplibre.compose.camera.internal.CameraCommandGuard
import org.maplibre.compose.camera.internal.CameraInputAuthority
import org.maplibre.compose.expressions.ast.CompiledExpression
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.ast.ExpressionContext
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.interaction.internal.RecognizedMapInput
import org.maplibre.compose.interaction.internal.select
import org.maplibre.compose.layers.LayerHandle
import org.maplibre.compose.layers.layerHandle
import org.maplibre.compose.logging.MapLog
import org.maplibre.compose.offline.OfflineManager
import org.maplibre.compose.offline.RuntimeBoundOfflineManager
import org.maplibre.compose.offline.UnsupportedOfflineManager
import org.maplibre.compose.resource.MapResourceConfig
import org.maplibre.compose.sources.Source
import org.maplibre.compose.sources.SourceHandle
import org.maplibre.compose.sources.sourceHandle
import org.maplibre.compose.style.BaseStyle
import org.maplibre.compose.style.DesiredStyleRevision
import org.maplibre.compose.style.LayerSummary
import org.maplibre.compose.style.Light
import org.maplibre.compose.style.Projection
import org.maplibre.compose.style.Sky
import org.maplibre.compose.style.SourceDefinition
import org.maplibre.compose.style.StyleBinding
import org.maplibre.compose.style.StyleHandleException
import org.maplibre.compose.style.StyleHandleOperationGuard
import org.maplibre.compose.style.StyleMutationException
import org.maplibre.compose.style.StyleResourceChanges
import org.maplibre.compose.style.TransitionOptions
import org.maplibre.compose.style.scaledBy
import org.maplibre.compose.style.systemAnimatorDurationScale
import org.maplibre.compose.style.withScaledTransitions
import org.maplibre.compose.util.ImageStretch
import org.maplibre.compose.util.MaplibreComposable
import org.maplibre.compose.util.VisibleBounds
import org.maplibre.compose.util.VisibleRegion
import org.maplibre.spatialk.geojson.BoundingBox
import org.maplibre.spatialk.geojson.Feature
import org.maplibre.spatialk.geojson.Geometry
import org.maplibre.spatialk.geojson.Position

/**
 * Configuration for one [MapRuntime].
 *
 * Every platform accepts a request interceptor and a resource provider, fixed for the lifetime of
 * the runtime. They apply to its maps, snapshotters, and supported offline operations. The MapLibre
 * Native platforms also accept a cache file and a cache size limit.
 */
public expect class MapRuntimeOptions

/** The options a runtime uses when nothing configures it. */
internal expect fun defaultMapRuntimeOptions(): MapRuntimeOptions

/** Creates a runtime from [options]. The caller must close the result. */
public expect fun createMapRuntime(options: MapRuntimeOptions): MapRuntime

/** Creates logical maps that share one application-level configuration. */
public interface MapRuntime {
  /** The offline packs and ambient cache managed by this runtime. */
  public val offlineManager: OfflineManager

  /**
   * Creates a logical map with [baseStyle] and the sources, layers, and images that [content]
   * declares. The caller must close the result.
   *
   * [content] reads the returned state through [LocalMapState] and its viewport through
   * [LocalViewport].
   */
  public fun createMapState(
    baseStyle: BaseStyle,
    cameraPosition: CameraPosition = CameraPosition(),
    content: @Composable @MaplibreComposable () -> Unit = {},
  ): MapState

  /**
   * Creates an independent non-UI map with [baseStyle] and the sources, layers, and images that
   * [content] declares, for image capture. The caller must close the result.
   *
   * [content] reads the viewport of each capture request through [LocalViewport]. It has no
   * [MapState], so [LocalMapState] is null.
   */
  public fun createSnapshotter(
    baseStyle: BaseStyle,
    content: @Composable @MaplibreComposable () -> Unit = {},
  ): MapSnapshotter

  /** Returns true after [close] marks this runtime as closed. */
  public val isClosed: Boolean

  /** Marks this runtime as closed and starts child and shared-resource cleanup. */
  public fun close()

  /**
   * Waits until every child and shared resource has finished cleanup.
   *
   * @throws MapCleanupException if cleanup fails.
   */
  public suspend fun awaitClosed()
}

/** Reports the load state for the desired base style of one logical map. */
public sealed interface StyleLoadState {
  /** No map surface can currently load the desired style. */
  public data object Pending : StyleLoadState

  /** Indicates that the current map surface is loading the desired style. */
  public data object Loading : StyleLoadState

  /**
   * The base style and initial composed content are ready. Ordinary content updates preserve this
   * state; it does not indicate that tiles or animations have finished rendering.
   */
  public data object Ready : StyleLoadState

  /** Loading the style or applying its composed content failed. A later revision may recover. */
  public data class Failed(public val reason: String?) : StyleLoadState
}

internal interface MapStyleStateOwner {
  fun setBaseStyle(value: BaseStyle)

  fun desiredSourceDefinition(id: String): org.maplibre.compose.style.SourceDefinition?

  fun isSourceWritable(id: String): Boolean

  fun isLayerWritable(id: String): Boolean

  fun isImageWritable(id: String): Boolean

  fun requireSourceWritable(id: String)

  fun requireLayerWritable(id: String)

  fun addStyleSource(source: Source): SourceHandle

  fun removeStyleSource(id: String, expectedStyle: StyleBinding, identity: Any): Boolean

  fun addStyleImage(
    id: String,
    image: ImageBitmap,
    sdf: Boolean,
    stretch: ImageStretch?,
  ): StyleImageHandle

  fun removeStyleImage(id: String, expectedStyle: StyleBinding, identity: Any): Boolean

  fun readyLoadedStyle(): StyleBinding?

  fun <T> runStyleHandleOperation(binding: StyleBinding, action: () -> T): T
}

/** Desired and applied style state for one logical map or snapshotter. */
public class MapStyleState internal constructor(baseStyle: BaseStyle) {
  private var owner: MapStyleStateOwner? = null
  private val loadedStyle = AtomicReference<StyleBinding?>(null)
  private var sourcesState: Map<String, SourceHandle> by
    mutableStateOf(emptyMap(), referentialEqualityPolicy())
  private var layersState: Map<String, LayerHandle> by
    mutableStateOf(emptyMap(), referentialEqualityPolicy())
  private var baseStyleState: BaseStyle by mutableStateOf(baseStyle, structuralEqualityPolicy())

  internal var baseStyleDeclared: Boolean = false

  /** The desired base style. A change replaces generation-bound resources. */
  public val baseStyle: BaseStyle
    get() = baseStyleState

  /** Base-style commands, or null when [rememberMapState] owns the base style. */
  public val asMutable: MutableMapStyleState?
    get() = if (baseStyleDeclared) null else MutableMapStyleState(this)

  internal fun updateBaseStyle(value: BaseStyle) {
    owner?.setBaseStyle(value) ?: setBaseStyleState(value)
  }

  public var loadState: StyleLoadState by mutableStateOf(StyleLoadState.Pending)
    internal set

  /** Sources in the current loaded-style generation. */
  public val sources: StyleSources = StyleSources(this)

  /** Layers in the current loaded-style generation. */
  public val layers: StyleLayers = StyleLayers(this)

  /** Style-image commands for the current loaded-style generation. */
  public val images: StyleImages = StyleImages(this)

  /** Global transition of the current loaded-style generation. */
  public val transition: StyleTransition = StyleTransition(this)

  /** Light of the current loaded-style generation. */
  public val light: StyleLight = StyleLight(this)

  /** Sky of the current loaded-style generation. */
  public val sky: StyleSky = StyleSky(this)

  /** Projection of the current loaded-style generation. */
  public val projection: StyleProjection = StyleProjection(this)

  internal suspend fun transitionOptions(): TransitionOptions? = readStyle { it.transition() }

  internal fun setTransitionOptions(options: TransitionOptions) {
    mutateStyle { it.setTransition(options.scaledBy(it.animatorDurationScale)) }
  }

  internal suspend fun placementTransitions(): Boolean? = readStyle { it.placementTransitions() }

  internal fun setPlacementTransitions(enabled: Boolean) {
    mutateStyle { it.setPlacementTransitions(enabled) }
  }

  internal suspend fun lightProperty(name: String): JsonElement? = readStyle {
    it.lightProperty(name)
  }

  internal fun setLight(light: Light) {
    mutateStyle { it.setLight(light.toJson().withScaledTransitions(it.animatorDurationScale)) }
  }

  internal suspend fun skyProperty(name: String): JsonElement? = readStyle { it.skyProperty(name) }

  internal fun setSky(sky: Sky?) {
    mutateStyle { it.setSky(sky?.toJson()?.withScaledTransitions(it.animatorDurationScale)) }
  }

  internal suspend fun projectionProperty(name: String): JsonElement? = readStyle {
    it.projectionProperty(name)
  }

  internal fun setProjection(projection: Projection) {
    mutateStyle { it.setProjection(projection.toJson()) }
  }

  /**
   * Reads from the ready loaded style, or returns null without one. A style that stops being ready
   * while the engine answers also reads as null: the value belongs to a generation that is gone.
   */
  private suspend fun <T> readStyle(read: suspend (StyleBinding) -> T?): T? {
    val current = readyLoadedStyle() ?: return null
    operationGuard(current).run {}
    val result = read(current)
    return result.takeIf { readyLoadedStyle() === current }
  }

  /** Posts a write to the ready loaded style. The engine reports a rejection through the logger. */
  private fun mutateStyle(mutate: (StyleBinding) -> Unit) {
    val current = checkNotNull(readyLoadedStyle()) { "No ready loaded style" }
    operationGuard(current).run { mutate(current) }
  }

  internal fun sourceHandle(id: String): SourceHandle? {
    if (readyLoadedStyle() == null) return null
    return sourcesState[id]
  }

  private fun sourceHandle(current: StyleBinding, id: String): SourceHandle? = owner.let { owner ->
    val definition = owner?.desiredSourceDefinition(id)
    val identity = current.identity.sources.get(id)
    current.sourceHandle(
      id = id,
      definition = definition,
      currentDefinition = { owner?.desiredSourceDefinition(id) },
      isCurrentResource = { current.identity.sources.isCurrent(id, identity) },
      operations = operationGuard(current),
    )
  }

  internal fun layerHandle(id: String): LayerHandle? {
    if (readyLoadedStyle() == null) return null
    return layersState[id]
  }

  internal fun readyLoadedStyle(): StyleBinding? {
    if (loadState != StyleLoadState.Ready) return null
    // With an owner attached, its serialized check is the only authority: a plain fallback to the
    // stored reference could return a binding the owner has already moved to Loading.
    val owner = owner ?: return loadedStyle.load()
    return owner.readyLoadedStyle()
  }

  internal fun attach(owner: MapStyleStateOwner) {
    this.owner = owner
  }

  internal fun requireOwner(): MapStyleStateOwner = checkNotNull(owner)

  internal fun setBaseStyleState(value: BaseStyle) {
    baseStyleState = value
  }

  internal fun updateLoadedStyle(style: StyleBinding?) {
    loadedStyle.store(style)
    sourcesState = emptyMap()
    layersState = emptyMap()
  }

  internal fun invalidateLoadedStyle() {
    loadedStyle.exchange(null)?.invalidate()
    sourcesState = emptyMap()
    layersState = emptyMap()
  }

  internal fun isCurrentLoadedStyle(style: StyleBinding): Boolean = loadedStyle.load() === style

  internal fun currentLoadedStyle(): StyleBinding? = loadedStyle.load()

  internal fun refreshResources() {
    val current = loadedStyle.load()
    if (loadState != StyleLoadState.Ready || current == null) {
      sourcesState = emptyMap()
      layersState = emptyMap()
    } else {
      updateResources(readResources(current))
    }
  }

  internal fun readResources(current: StyleBinding): LoadedStyleResources =
    LoadedStyleResources(readSources(current), readLayers(current))

  internal fun readSources(
    current: StyleBinding,
    changedId: String? = null,
  ): Map<String, SourceHandle> {
    if (changedId != null) {
      val handle = sourceHandle(current, changedId)
      val handles = sourcesState.toMutableMap()
      if (handle == null) handles.remove(changedId) else handles[changedId] = handle
      val ids = current.sourceIds()
      current.identity.sources.retain(ids.toSet())
      return ids.mapNotNull { id -> handles[id]?.let { id to it } }.toMap()
    }
    val ids = current.sourceIds().toSet()
    current.identity.sources.retain(ids)
    return ids.mapNotNull { id -> sourceHandle(current, id)?.let { id to it } }.toMap()
  }

  internal fun updateSources(sources: Map<String, SourceHandle>) {
    sourcesState = sources
  }

  internal fun readLayers(current: StyleBinding): Map<String, LayerHandle> {
    val summaries = current.layerSummaries()
    current.identity.layers.retain(summaries.keys)
    return summaries.mapValues { (id, summary) -> layerHandle(current, id, summary) }
  }

  /** Rereads the handles of [ids] in one engine round trip; a removed layer maps to null. */
  internal fun readLayers(current: StyleBinding, ids: Set<String>): Map<String, LayerHandle?> {
    if (ids.isEmpty()) return emptyMap()
    val summaries = current.layerSummaries()
    return ids.associateWith { id -> summaries[id]?.let { layerHandle(current, id, it) } }
  }

  internal fun layerHandle(current: StyleBinding, id: String, summary: LayerSummary): LayerHandle {
    val identity = current.identity.layers.get(id)
    return current.layerHandle(
      id,
      summary,
      isCurrentResource = { current.identity.layers.isCurrent(id, identity) },
      operations = operationGuard(current),
    )
  }

  internal fun updateLayers(handles: Map<String, LayerHandle?>, order: List<String>) {
    val updated = layersState.toMutableMap()
    handles.forEach { (id, handle) ->
      if (handle == null) updated.remove(id) else updated[id] = handle
    }
    layersState = order.mapNotNull { id -> updated[id]?.let { id to it } }.toMap()
  }

  internal fun updateResources(resources: LoadedStyleResources) {
    sourcesState = resources.sources
    layersState = resources.layers
  }

  internal fun sourceHandles(): Map<String, SourceHandle> =
    if (readyLoadedStyle() == null) emptyMap() else sourcesState

  internal fun layerHandles(): Map<String, LayerHandle> =
    if (readyLoadedStyle() == null) emptyMap() else layersState

  internal fun operationGuard(style: StyleBinding): StyleHandleOperationGuard =
    object : StyleHandleOperationGuard {
      override fun <T> run(action: () -> T): T =
        owner?.runStyleHandleOperation(style, action) ?: action()

      override fun isSourceWritable(id: String): Boolean = owner?.isSourceWritable(id) == true

      override fun isLayerWritable(id: String): Boolean = owner?.isLayerWritable(id) == true

      override fun removeSource(id: String, identity: Any): Boolean =
        requireOwner().removeStyleSource(id, style, identity)

      override fun requireSourceWritable(id: String) {
        owner?.requireSourceWritable(id)
      }

      override fun requireLayerWritable(id: String) {
        owner?.requireLayerWritable(id)
      }
    }
}

internal data class LoadedStyleResources(
  val sources: Map<String, SourceHandle>,
  val layers: Map<String, LayerHandle>,
)

internal class ImperativeSourceRecord(val definition: SourceDefinition)

internal class ImperativeImageRecord(val fromResolver: Boolean = false)

/**
 * One missing-image resolution, identified by [token] so a stale one cannot evict its successor.
 */
internal class MissingImageResolution(val token: Any, val work: Deferred<Unit>)

internal class StyleMutationReservation {
  val completion = CompletableDeferred<Unit>()
}

/** Connects a [MapState] to one map surface for the lifetime of one render lease. */
internal class MapAttachment
internal constructor(
  private val owner: MapState,
  internal val token: MapPresentationToken,
  internal val adapter: MapAdapter,
) {
  private val invalidated = CompletableDeferred<Unit>()
  private var validState: Boolean by mutableStateOf(true)
  private var viewportState: Viewport? by mutableStateOf(null)
  private val firstViewport = CompletableDeferred<Viewport>()
  private var gestureActiveState: Boolean by mutableStateOf(false)
  private var cameraChangingState: Boolean by mutableStateOf(false)
  private var moveReasonState: CameraMoveReason by mutableStateOf(CameraMoveReason.NONE)
  private var engagedState: Boolean by mutableStateOf(false)
  val isValid: Boolean
    get() = validState

  val isEngaged: Boolean
    get() = engagedState

  val viewport: Viewport?
    get() = viewportState

  val isCameraMoving: Boolean
    get() = gestureActiveState || cameraChangingState

  val cameraMoveReason: CameraMoveReason
    get() = moveReasonState

  suspend fun cameraForBounds(
    boundingBox: BoundingBox,
    bearing: Double,
    tilt: Double,
    padding: PaddingValues,
  ): CameraPosition = runLeaseBound {
    awaitViewportState()
    adapter.cameraForBounds(boundingBox, bearing, tilt, padding)
  }

  suspend fun fitCameraToBounds(
    boundingBox: BoundingBox,
    bearing: Double = 0.0,
    tilt: Double = 0.0,
    padding: PaddingValues = PaddingValues(0.dp),
    guard: CameraCommandGuard? = null,
  ): Unit = runLeaseBound {
    awaitViewportState()
    adapter.fitCameraToBounds(boundingBox, bearing, tilt, padding, boundGuard(guard))
  }

  suspend fun animateCameraPosition(
    position: CameraPosition,
    duration: Duration = 300.milliseconds,
    guard: CameraCommandGuard? = null,
  ): Unit = runLeaseBound {
    awaitViewportState()
    adapter.animateCameraPosition(position, duration, boundGuard(guard))
  }

  suspend fun animateCameraToBounds(
    boundingBox: BoundingBox,
    bearing: Double = 0.0,
    tilt: Double = 0.0,
    padding: PaddingValues = PaddingValues(0.dp),
    duration: Duration = 300.milliseconds,
    guard: CameraCommandGuard? = null,
  ): Unit = runLeaseBound {
    awaitViewportState()
    adapter.animateCameraToBounds(boundingBox, bearing, tilt, padding, duration, boundGuard(guard))
  }

  fun getVisibleRegion(): VisibleRegion? = withViewport { it.getVisibleRegion() }

  fun getVisibleBounds(): VisibleBounds? = withViewport { it.getVisibleBounds() }

  fun screenLocationFromPosition(position: Position): DpOffset? = withViewport {
    it.screenLocationFromPosition(position)
  }

  fun positionFromScreenLocation(offset: DpOffset): Position? = withViewport {
    it.positionFromScreenLocation(offset)
  }

  fun metersPerDpAtLatitude(latitude: Double): Double? = withViewport {
    it.metersPerDpAtLatitude(latitude)
  }

  suspend fun queryRenderedFeatures(
    offset: DpOffset,
    layerIds: Set<String>? = null,
    predicate: Expression<BooleanValue> = const(true),
  ): List<Feature<Geometry, JsonObject?>> = runLeaseBound {
    awaitViewportState()
    adapter.queryRenderedFeatures(offset, layerIds, predicate.compileOrNull())
  }

  suspend fun queryRenderedFeatures(
    rect: DpRect,
    layerIds: Set<String>? = null,
    predicate: Expression<BooleanValue> = const(true),
  ): List<Feature<Geometry, JsonObject?>> = runLeaseBound {
    awaitViewportState()
    adapter.queryRenderedFeatures(rect, layerIds, predicate.compileOrNull())
  }

  suspend fun awaitViewport(): Viewport = runLeaseBound { awaitViewportState() }

  internal fun updateViewport(value: Viewport?) {
    owner.lifecycle.serialized {
      viewportState = value
      value?.let(firstViewport::complete)
    }
  }

  /**
   * A gesture sets the reason even while an engine camera change is in flight, because the gesture
   * token decides whether a change belongs to the user.
   */
  internal fun setGestureActive(active: Boolean) {
    owner.lifecycle.serialized {
      gestureActiveState = active
      if (active) moveReasonState = CameraMoveReason.GESTURE
    }
  }

  internal fun setEngaged(engaged: Boolean) {
    owner.lifecycle.serialized { engagedState = engaged }
  }

  internal fun cameraChangeStarted() {
    owner.lifecycle.serialized {
      cameraChangingState = true
      if (!gestureActiveState) moveReasonState = CameraMoveReason.PROGRAMMATIC
    }
  }

  internal fun cameraChangeEnded() {
    owner.lifecycle.serialized { cameraChangingState = false }
  }

  internal fun invalidate() {
    owner.gestureAuthority.detach(this)
    owner.lifecycle.serialized {
      validState = false
      viewportState = null
      gestureActiveState = false
      cameraChangingState = false
      engagedState = false
      invalidated.complete(Unit)
    }
  }

  private fun Expression<BooleanValue>.compileOrNull(): CompiledExpression<BooleanValue>? {
    if (this == const(true)) return null
    return compile(ExpressionContext.None)
  }

  private fun <T> withViewport(block: (MapAdapter) -> T): T? =
    owner.withCurrentOrNull(this) { if (viewportState == null) null else block(adapter) }

  private fun boundGuard(guard: CameraCommandGuard?): CameraCommandGuard = CameraCommandGuard {
    owner.isCurrent(this) && guard?.isValid() != false
  }

  private suspend fun awaitViewportState(): Viewport = firstViewport.await()

  private suspend fun <T> runLeaseBound(block: suspend () -> T): T = coroutineScope {
    if (!owner.isCurrent(this@MapAttachment)) throw MapAttachmentChangedException()
    val operation =
      async(start = CoroutineStart.UNDISPATCHED) {
        if (!owner.isCurrent(this@MapAttachment)) throw MapAttachmentChangedException()
        block()
      }
    select {
      operation.onAwait { result ->
        // An inline operation can finish after invalidation, before select starts listening.
        if (!owner.isCurrent(this@MapAttachment)) throw MapAttachmentChangedException()
        result
      }
      invalidated.onAwait {
        operation.cancelAndJoin()
        throw MapAttachmentChangedException()
      }
    }
  }
}

private class MapAttachmentChangedException :
  CancellationException("The map attachment changed during the operation")

/** Holds the observable style, camera, and map operations for one logical map. */
@Stable
public class MapState
internal constructor(
  internal val runtime: RuntimeImplementation,
  cameraPosition: CameraPosition,
  baseStyle: BaseStyle,
  content: @Composable @MaplibreComposable () -> Unit,
) {
  internal val styleContent: @Composable @MaplibreComposable () -> Unit = {
    CompositionLocalProvider(LocalMapState provides this, LocalViewport provides viewport) {
      content()
    }
  }
  internal val lifecycle = MapLifecycleAuthority(this, runtime.physicalScope)
  internal val gestureAuthority = CameraInputAuthority(this)
  /** Set by the current presentation; recognized gestures are ignored without one. */
  internal var recognizedInput: RecognizedMapInput? = null
  private var baseStyleCommandRevision = 0L
  private var cameraCommandRevision = 0L
  private var styleHandleEpoch = 0L
  private var styleSourceChangeRevision = 0L
  private val imperativeSources = mutableMapOf<String, ImperativeSourceRecord>()
  private val imperativeImages = mutableMapOf<String, ImperativeImageRecord>()
  private var missingImageResolverState: MissingImageResolver? by mutableStateOf(null)
  /** Resolutions started for the loaded style, by image id. */
  private val missingImageResolutions = mutableMapOf<String, MissingImageResolution>()
  private var activeStyleMutation: StyleMutationReservation? = null
  /**
   * Held while a resolved missing image reaches the style. A style command the app issues cannot
   * anticipate this one, so the command waits it out through [beginStyleRevision] or runs beside it
   * rather than failing on it.
   */
  private var backgroundStyleMutation: StyleMutationReservation? = null
  private val eventsFlow =
    MutableSharedFlow<MapEvent>(
      extraBufferCapacity = 64,
      onBufferOverflow = BufferOverflow.DROP_OLDEST,
    )
  private var closedState: Boolean by mutableStateOf(false)
  private var cameraPositionState: CameraPosition by
    mutableStateOf(cameraPosition, structuralEqualityPolicy())

  internal var desiredStyleRevision: DesiredStyleRevision = DesiredStyleRevision.Empty

  public val style: MapStyleState =
    MapStyleState(baseStyle).also {
      it.attach(
        object : MapStyleStateOwner {
          override fun setBaseStyle(value: BaseStyle) = this@MapState.setBaseStyle(value)

          override fun desiredSourceDefinition(id: String) =
            this@MapState.desiredSourceDefinition(id)

          override fun isSourceWritable(id: String): Boolean = lifecycle.serialized {
            desiredStyleRevision.sources.none { it.id == id }
          }

          override fun isLayerWritable(id: String): Boolean = lifecycle.serialized {
            desiredStyleRevision.layers.none { it.definition.id == id }
          }

          override fun isImageWritable(id: String): Boolean = lifecycle.serialized {
            desiredStyleRevision.images.none { it.id == id }
          }

          override fun requireSourceWritable(id: String) = lifecycle.serialized {
            requireNoDesiredSource(id)
          }

          override fun requireLayerWritable(id: String) = lifecycle.serialized {
            if (desiredStyleRevision.layers.any { it.definition.id == id }) {
              throw StyleHandleException("Layer ID '$id' is declared by the style content")
            }
          }

          override fun addStyleSource(source: Source) = this@MapState.addStyleSource(source)

          override fun removeStyleSource(id: String, expectedStyle: StyleBinding, identity: Any) =
            this@MapState.removeStyleSource(id, expectedStyle, identity)

          override fun addStyleImage(
            id: String,
            image: ImageBitmap,
            sdf: Boolean,
            stretch: ImageStretch?,
          ) = this@MapState.addStyleImage(id, image, sdf, stretch)

          override fun removeStyleImage(id: String, expectedStyle: StyleBinding, identity: Any) =
            this@MapState.removeStyleImage(id, expectedStyle, identity)

          override fun readyLoadedStyle() = this@MapState.readyLoadedStyle()

          override fun <T> runStyleHandleOperation(
            binding: StyleBinding,
            action: () -> T,
          ): T = this@MapState.runStyleHandleOperation(binding, action)
        }
      )
    }

  public val cameraPosition: CameraPosition
    get() = cameraPositionState

  internal var currentMapAttachment: MapAttachment? by mutableStateOf(null)
    internal set

  private var nextMapAttachment = CompletableDeferred<MapAttachment>()

  /** Contains the current rendered viewport, or null while no viewport is available. */
  public val viewport: Viewport?
    get() = currentMapAttachment?.viewport

  /**
   * Returns true while a gesture holds the camera or an engine camera change is in flight. During a
   * drag the gesture stays active across every camera change the engine reports, so the value stays
   * true for the whole drag.
   */
  public val isCameraMoving: Boolean
    get() = currentMapAttachment?.isCameraMoving == true

  /**
   * Contains what started the most recent camera movement, or [CameraMoveReason.NONE] while
   * detached and before the first movement. The value stays after the movement ends.
   */
  public val cameraMoveReason: CameraMoveReason
    get() = currentMapAttachment?.cameraMoveReason ?: CameraMoveReason.NONE

  /**
   * Returns true while the focused map consumes the keys that pan, zoom, rotate, and tilt. Enter,
   * numpad Enter, D-pad center, and a pointer press engage the map. Escape disengages it, and Back
   * disengages it when a key engaged it. Focus loss disengages it. A focused map that is not
   * engaged passes direction keys to focus traversal. The value is false while no map surface is
   * attached.
   */
  public val isEngaged: Boolean
    get() = currentMapAttachment?.isEngaged == true

  /**
   * Reports [MapEvent]s that occur after collection starts. Past events are not replayed, and slow
   * collectors may miss events.
   *
   * Style and idle events can continue while a native map has no attached surface. Camera and frame
   * events require an attached surface.
   *
   * Unconfined collectors may run inside engine callbacks. Use a dispatcher that queues execution
   * for collectors that call map commands such as [StyleImages.add].
   */
  public val events: Flow<MapEvent> = eventsFlow.asSharedFlow()

  /**
   * Supplies missing style images on demand. Null (the default) disables resolution.
   *
   * Return a [ResolvedStyleImage] for the requested ID, suspending if it needs to be loaded. Be
   * prepared to supply the same ID again after the map discards unused images. On native maps,
   * resolved images may appear only after the affected tiles are laid out again.
   *
   * Return null for IDs you cannot supply. Null results and exceptions are not retried until the
   * base style reloads or the resolver is replaced.
   *
   * Replacing or clearing this property does not cancel calls already running.
   */
  public var missingImageResolver: MissingImageResolver?
    get() = missingImageResolverState
    set(value) {
      lifecycle.serialized {
        if (missingImageResolverState === value) return
        missingImageResolverState = value
        // Forgotten rather than cancelled: the request that started a resolution in flight is still
        // outstanding, and the engine waits on that resolution rather than asking the new resolver.
        missingImageResolutions.clear()
      }
    }

  public val isClosed: Boolean
    get() = closedState

  /** Marks this state as closed and starts cleanup of the current map surface. */
  public fun close(): Unit = lifecycle.close()

  /**
   * Waits until map-surface cleanup has completed.
   *
   * @throws MapCleanupException if cleanup fails.
   */
  public suspend fun awaitClosed(): Unit = lifecycle.awaitClosed()

  /**
   * Sets the durable camera position and applies it to the current surface when one is attached.
   */
  public fun setCameraPosition(position: CameraPosition) {
    val guard = gestureAuthority.beginProgrammatic()
    val command = lifecycle.serialized {
      requireOpenLocked()
      if (!guard.isValid()) return
      cameraPositionState = position
      cameraCommandRevision++
      val attachment = currentMapAttachment ?: return
      AttachmentCameraCommand(
        attachment = attachment,
        command = CameraCommand(attachment.adapter, position, cameraCommandRevision, guard),
      )
    }
    applyAttachmentCameraCommand(command.attachment, command.command)
  }

  /**
   * Waits for a viewport, then calculates a camera for [boundingBox] without moving the map or
   * interrupting camera input or animations. Detaching the surface during the query cancels it.
   *
   * [padding] adds space around the bounds in addition to the map's camera padding. It does not
   * change the map's padding. The result uses the current viewport size and camera constraints;
   * recalculate it if those or the map's padding change before applying it.
   *
   * On the browser, fitting calculates the target and zoom without [tilt], then assigns [tilt] to
   * the result. A nonzero tilt may therefore leave part of the bounds outside the viewport.
   *
   * @throws IllegalStateException if the backend cannot calculate a camera for the bounds.
   */
  public suspend fun cameraForBounds(
    boundingBox: BoundingBox,
    bearing: Double = 0.0,
    tilt: Double = 0.0,
    padding: PaddingValues = PaddingValues(0.dp),
  ): CameraPosition = awaitAttachment().cameraForBounds(boundingBox, bearing, tilt, padding)

  /**
   * Waits for a viewport, then fits [boundingBox] without animation. A newer camera command or
   * accepted input cancels this call.
   */
  public suspend fun fitCameraToBounds(
    boundingBox: BoundingBox,
    bearing: Double = 0.0,
    tilt: Double = 0.0,
    padding: PaddingValues = PaddingValues(0.dp),
  ): Unit = coroutineScope {
    val guard = gestureAuthority.beginProgrammatic(currentCoroutineContext()[Job])
    retryAcrossAttachments { it.fitCameraToBounds(boundingBox, bearing, tilt, padding, guard) }
  }

  /**
   * Waits for a viewport, then animates to [position]. A newer camera command or accepted input
   * cancels this call.
   *
   * On Android, the system animator duration scale multiplies [duration]. A scale of zero jumps to
   * [position].
   */
  public suspend fun animateCameraPosition(
    position: CameraPosition,
    duration: Duration = 300.milliseconds,
  ): Unit = coroutineScope {
    val guard = gestureAuthority.beginProgrammatic(currentCoroutineContext()[Job])
    retryAcrossAttachments {
      it.animateCameraPosition(position, duration.scaledBy(systemAnimatorDurationScale()), guard)
    }
  }

  /**
   * Waits for a viewport, then animates to fit [boundingBox]. A newer camera command or accepted
   * input cancels this call.
   *
   * On Android, the system animator duration scale multiplies [duration]. A scale of zero jumps to
   * fit [boundingBox].
   */
  public suspend fun animateCameraToBounds(
    boundingBox: BoundingBox,
    bearing: Double = 0.0,
    tilt: Double = 0.0,
    padding: PaddingValues = PaddingValues(0.dp),
    duration: Duration = 300.milliseconds,
  ): Unit = coroutineScope {
    val guard = gestureAuthority.beginProgrammatic(currentCoroutineContext()[Job])
    retryAcrossAttachments {
      it.animateCameraToBounds(
        boundingBox,
        bearing,
        tilt,
        padding,
        duration.scaledBy(systemAnimatorDurationScale()),
        guard,
      )
    }
  }

  /**
   * Pans the map by [delta] in logical pixels, as a gesture would. A positive x moves the content
   * right.
   *
   * [panBy], [scaleBy], [fling], and [click] pass gestures that your code recognized. They follow
   * the camera permissions and callbacks in [org.maplibre.compose.interaction.MapInteractions],
   * interrupt a camera animation in progress, and report [CameraMoveReason.GESTURE]. They do
   * nothing while no map is presented.
   */
  public fun panBy(delta: DpOffset) {
    recognizedInput?.pan(delta)
  }

  /**
   * Scales the map by [factor], as a gesture would, keeping [anchor] fixed on screen. A null anchor
   * scales about the viewport center. See [panBy].
   */
  public fun scaleBy(factor: Double, anchor: DpOffset? = null) {
    recognizedInput?.scale(factor, anchor)
  }

  /**
   * Continues a pan at [velocity], in logical pixels per second, until the momentum settles or
   * newer input interrupts it. Velocities below the momentum threshold are ignored. See [panBy].
   */
  public fun fling(velocity: DpOffset) {
    recognizedInput?.fling(velocity)
  }

  /**
   * Dispatches a click at [offset], in logical pixels, to the click callbacks and interactive
   * layers. See [panBy].
   */
  public fun click(offset: DpOffset) {
    recognizedInput?.click(offset)
  }

  /** Returns the visible region, or null while no viewport is available. */
  public fun getVisibleRegion(): VisibleRegion? =
    withAttachmentRead(MapAttachment::getVisibleRegion)

  /** Returns the visible axis-aligned bounds, or null while no viewport is available. */
  public fun getVisibleBounds(): VisibleBounds? =
    withAttachmentRead(MapAttachment::getVisibleBounds)

  /**
   * Projects [position] into a logical-pixel offset, or returns null without a viewport.
   *
   * Longitudes equivalent modulo 360° project onto the world copy nearest the camera target.
   */
  public fun screenLocationFromPosition(position: Position): DpOffset? = withAttachmentRead {
    it.screenLocationFromPosition(position)
  }

  /**
   * Unprojects [offset] into a geographic position, or returns null without a viewport.
   *
   * Longitude preserves the visible world copy and may extend past ±180°.
   */
  public fun positionFromScreenLocation(offset: DpOffset): Position? = withAttachmentRead {
    it.positionFromScreenLocation(offset)
  }

  /** Returns the ground distance per dp, or null while no viewport is available. */
  public fun metersPerDpAtLatitude(latitude: Double): Double? = withAttachmentRead {
    it.metersPerDpAtLatitude(latitude)
  }

  /**
   * Waits for a viewport, then queries rendered features at [offset] in front-to-back render order.
   * Detaching the map surface during the query cancels it.
   *
   * A geometry that crosses the antimeridian may come back split into pieces, with longitudes past
   * ±180° in either direction. When several world copies are visible, the same source feature can
   * appear once per copy it occupies in the query area.
   */
  public suspend fun queryRenderedFeatures(
    offset: DpOffset,
    layerIds: Set<String>? = null,
    predicate: Expression<BooleanValue> = const(true),
  ): List<Feature<Geometry, JsonObject?>> =
    awaitAttachment().queryRenderedFeatures(offset, layerIds, predicate)

  /**
   * Waits for a viewport, then queries rendered features that intersect [rect] in front-to-back
   * render order. Detaching the map surface during the query cancels it.
   *
   * A geometry that crosses the antimeridian may come back split into pieces, with longitudes past
   * ±180° in either direction. When several world copies are visible, the same source feature can
   * appear once per copy it occupies in the query area.
   */
  public suspend fun queryRenderedFeatures(
    rect: DpRect,
    layerIds: Set<String>? = null,
    predicate: Expression<BooleanValue> = const(true),
  ): List<Feature<Geometry, JsonObject?>> =
    awaitAttachment().queryRenderedFeatures(rect, layerIds, predicate)

  /** Waits for the first viewport from the current or a future map attachment. */
  public suspend fun awaitViewport(): Viewport =
    retryAcrossAttachments(MapAttachment::awaitViewport)

  private suspend fun <T> retryAcrossAttachments(operation: suspend (MapAttachment) -> T): T {
    var attachment = awaitAttachment()
    while (true) {
      try {
        return operation(attachment)
      } catch (_: MapAttachmentChangedException) {
        attachment = awaitReplacementAttachment()
      }
    }
  }

  internal fun reservePresentation(
    owner: MapPresentationOwnerToken = MapPresentationOwnerToken()
  ): MapPresentationToken = lifecycle.reservePresentation(owner)

  internal fun publishPresentation(
    token: MapPresentationToken,
    adapter: MapAdapter,
  ) = lifecycle.publishPresentation(token, adapter)

  internal fun releasePresentation(token: MapPresentationToken, adapter: MapAdapter? = null) =
    lifecycle.releasePresentation(token, adapter)

  internal fun retainedAdapter(compatibilityKey: Any): MapAdapter? =
    lifecycle.retainedAdapter(compatibilityKey)

  internal fun durableStyleCallbacks(): MapAdapter.Callbacks = DurableStyleCallbacks(this)

  internal fun markStyleReady(adapter: MapAdapter): Boolean {
    while (true) {
      val read = lifecycle.serialized {
        if (!lifecycle.acceptsAdapter(adapter)) return false
        if (style.loadState == StyleLoadState.Ready) return true
        val binding = style.currentLoadedStyle() ?: return false
        StyleResourceRead(binding, styleHandleEpoch, styleSourceChangeRevision)
      }
      val resources = runCatching { style.readResources(read.binding) }
      if (resources.isFailure) {
        val stillCurrent = lifecycle.serialized { isCurrentStyleResourceRead(adapter, read) }
        if (!stillCurrent) return false
        throw requireNotNull(resources.exceptionOrNull())
      }
      val committed = lifecycle.serialized {
        if (!isCurrentStyleResourceRead(adapter, read)) return false
        if (style.loadState is StyleLoadState.Failed) return false
        if (styleSourceChangeRevision != read.sourceChangeRevision) return@serialized false
        style.updateResources(resources.getOrThrow())
        style.loadState = StyleLoadState.Ready
        true
      }
      if (committed) return true
    }
  }

  internal fun refreshStyleSources(adapter: MapAdapter, sourceId: String? = null): Boolean {
    lifecycle.serialized {
      if (!lifecycle.acceptsAdapter(adapter)) return false
      styleSourceChangeRevision++
    }
    while (true) {
      val read = lifecycle.serialized {
        if (!lifecycle.acceptsAdapter(adapter)) return false
        if (style.loadState != StyleLoadState.Ready) return true
        val binding = style.currentLoadedStyle() ?: return true
        StyleResourceRead(binding, styleHandleEpoch, styleSourceChangeRevision)
      }
      val sources = runCatching { style.readSources(read.binding, sourceId) }
      if (sources.isFailure) {
        val stillCurrent = lifecycle.serialized { isCurrentStyleResourceRead(adapter, read) }
        if (!stillCurrent) return false
        throw requireNotNull(sources.exceptionOrNull())
      }
      val committed = lifecycle.serialized {
        if (!isCurrentStyleResourceRead(adapter, read)) return false
        if (style.loadState != StyleLoadState.Ready) return false
        if (styleSourceChangeRevision != read.sourceChangeRevision) return@serialized false
        style.updateSources(sources.getOrThrow())
        true
      }
      if (committed) return true
    }
  }

  internal fun updateStyleResources(adapter: MapAdapter, changes: StyleResourceChanges) {
    if (changes.sources.isEmpty() && changes.layerOrder == null) return
    val read = lifecycle.serialized {
      if (!lifecycle.acceptsAdapter(adapter) || style.loadState != StyleLoadState.Ready) return
      val binding = style.currentLoadedStyle() ?: return
      if (binding.identity !== changes.identity) return
      StyleResourceRead(binding, styleHandleEpoch, styleSourceChangeRevision)
    }
    changes.sources.forEach { refreshStyleSources(adapter, it) }
    val layers = runCatching { style.readLayers(read.binding, changes.layers) }
    lifecycle.serialized {
      if (!isCurrentStyleResourceRead(adapter, read)) return
      changes.layerOrder?.let { style.updateLayers(layers.getOrThrow(), it) }
    }
  }

  private fun isCurrentStyleResourceRead(adapter: MapAdapter, read: StyleResourceRead): Boolean =
    lifecycle.acceptsAdapter(adapter) &&
      styleHandleEpoch == read.styleHandleEpoch &&
      style.currentLoadedStyle() === read.binding

  internal fun updateLoadedStyle(adapter: MapAdapter, loadedStyle: StyleBinding?): Boolean =
    lifecycle.serialized {
      if (!lifecycle.acceptsAdapter(adapter)) return false
      if (style.currentLoadedStyle() === loadedStyle) return true
      styleHandleEpoch++
      imperativeSources.clear()
      imperativeImages.clear()
      cancelMissingImageResolutions()
      style.loadState = StyleLoadState.Loading
      style.updateLoadedStyle(loadedStyle)
      true
    }

  internal fun readyLoadedStyle(): StyleBinding? = lifecycle.serialized {
    style.currentLoadedStyle()?.takeIf { style.loadState == StyleLoadState.Ready }
  }

  internal fun markStyleFailed(adapter: MapAdapter, reason: String?) {
    lifecycle.serialized {
      if (lifecycle.acceptsAdapter(adapter)) {
        style.loadState = StyleLoadState.Failed(reason)
      }
    }
  }

  internal suspend fun beginStyleRevision(adapter: MapAdapter, revision: DesiredStyleRevision) {
    while (true) {
      val mutation = lifecycle.serialized {
        if (!lifecycle.acceptsAdapter(adapter)) return
        activeStyleMutation
          ?: backgroundStyleMutation
          ?: run {
            requireNoImperativeResourceConflicts(revision)
            styleHandleEpoch++
            if (style.loadState is StyleLoadState.Failed) {
              style.loadState = StyleLoadState.Loading
            }
            desiredStyleRevision = revision
            return
          }
      }
      mutation.completion.await()
    }
  }

  internal fun setBaseStyle(value: BaseStyle) {
    val command = lifecycle.serialized {
      requireOpenLocked()
      if (style.baseStyle == value) return
      requireNoActiveStyleMutation()
      styleHandleEpoch++
      imperativeSources.clear()
      imperativeImages.clear()
      cancelMissingImageResolutions()
      style.setBaseStyleState(value)
      style.invalidateLoadedStyle()
      val adapter = lifecycle.currentAdapter()
      if (adapter == null) {
        style.loadState = StyleLoadState.Pending
        baseStyleCommandRevision++
        return
      } else {
        style.loadState = StyleLoadState.Loading
      }
      BaseStyleCommand(adapter, value, ++baseStyleCommandRevision)
    }
    applyBaseStyleCommand(command)
  }

  internal fun desiredSourceDefinition(id: String): org.maplibre.compose.style.SourceDefinition? =
    lifecycle.serialized {
      desiredStyleRevision.sources.firstOrNull { it.id == id } ?: imperativeSources[id]?.definition
    }

  internal fun addStyleSource(source: Source): SourceHandle {
    val definition = source.definition()
    val record = ImperativeSourceRecord(definition)
    val reservation = StyleMutationReservation()
    val binding = lifecycle.serialized {
      requireOpenLocked()
      requireNoDesiredSource(source.id)
      requireNoActiveStyleMutation()
      if (source.id in imperativeSources) {
        throw StyleHandleException("Source ID '${source.id}' already exists in style")
      }
      checkNotNull(style.currentLoadedStyle()).also(::requireStyleHandleLocked).also {
        imperativeSources[source.id] = record
        activeStyleMutation = reservation
      }
    }
    var committed = false
    try {
      if (binding.sourceExists(source.id) == true) {
        throw StyleHandleException("Source ID '${source.id}' already exists in style")
      }
      val added = binding.addSource(definition)
      if (!added) throw IllegalStateException("The loaded-style generation changed during add")
      lifecycle.serialized { requireStyleHandleLocked(binding) }
      val handle = checkNotNull(refreshSourcesAfterCommand(binding)[source.id])
      committed = true
      return handle
    } catch (error: StyleMutationException) {
      throw StyleHandleException("Could not add source '${source.id}': ${error.message}", error)
    } finally {
      lifecycle.serialized {
        if (!committed && imperativeSources[source.id] === record) {
          imperativeSources.remove(source.id)
        }
        completeStyleMutation(reservation)
      }
    }
  }

  internal fun removeStyleSource(id: String, expectedStyle: StyleBinding, identity: Any): Boolean {
    val reservation = StyleMutationReservation()
    val binding = lifecycle.serialized {
      requireOpenLocked()
      requireStyleHandleLocked(expectedStyle)
      check(expectedStyle.identity.sources.isCurrent(id, identity)) {
        "Source '$id' has been removed or replaced"
      }
      requireNoDesiredSource(id)
      requireNoActiveStyleMutation()
      checkNotNull(style.currentLoadedStyle()).also(::requireStyleHandleLocked).also {
        activeStyleMutation = reservation
      }
    }
    try {
      if (binding.sourceExists(id) == false) return false
      binding.removeSource(id)
      lifecycle.serialized {
        requireStyleHandleLocked(binding)
        imperativeSources.remove(id)
        binding.identity.sources.remove(id)
      }
      refreshSourcesAfterCommand(binding)
      return true
    } catch (error: StyleMutationException) {
      throw StyleHandleException("Could not remove source '$id': ${error.message}", error)
    } finally {
      lifecycle.serialized { completeStyleMutation(reservation) }
    }
  }

  internal fun addStyleImage(
    id: String,
    image: ImageBitmap,
    sdf: Boolean,
    stretch: ImageStretch?,
  ): StyleImageHandle {
    val record = ImperativeImageRecord(fromResolver = false)
    val reservation = StyleMutationReservation()
    val binding = lifecycle.serialized {
      requireOpenLocked()
      requireNoDesiredImage(id)
      requireNoActiveStyleMutation()
      if (id in imperativeImages) {
        throw StyleHandleException("Image ID '$id' already exists in style")
      }
      checkNotNull(style.currentLoadedStyle()).also(::requireStyleHandleLocked).also {
        imperativeImages[id] = record
        activeStyleMutation = reservation
      }
    }
    var committed = false
    try {
      if (binding.imageExists(id) == true) {
        throw StyleHandleException("Image ID '$id' already exists in style")
      }
      binding.identity.images.remove(id)
      binding.addImage(id, image, sdf, stretch)
      val handle = lifecycle.serialized {
        requireStyleHandleLocked(binding)
        StyleImageHandleImpl(id, style, binding)
      }
      committed = true
      return handle
    } catch (error: StyleMutationException) {
      throw StyleHandleException("Could not add image '$id': ${error.message}", error)
    } finally {
      lifecycle.serialized {
        if (!committed && imperativeImages[id] === record) imperativeImages.remove(id)
        completeStyleMutation(reservation)
      }
    }
  }

  /**
   * Starts resolution of a missing style image and returns the resolution, or null when no resolver
   * is set, no style is loaded, or this state no longer accepts [adapter].
   *
   * A repeated request for an id already resolving returns that resolution, which the browser needs
   * to keep the request pending while the first call runs.
   *
   * The engine reports the miss from its own thread, so the resolution runs on this state's scope
   * rather than there.
   */
  internal fun resolveMissingImage(adapter: MapAdapter, imageId: String): Deferred<Unit>? =
    lifecycle.serialized {
      if (lifecycle.isClosed || !lifecycle.acceptsAdapter(adapter)) return@serialized null
      val resolver = missingImageResolverState ?: return@serialized null
      val binding = style.currentLoadedStyle() ?: return@serialized null
      if (hasDesiredImage(imageId) || imperativeImages[imageId]?.fromResolver == false)
        return@serialized null
      missingImageResolutions[imageId]?.let {
        return@serialized it.work
      }
      val token = Any()
      runtime.physicalScope
        .async(start = CoroutineStart.LAZY) {
          supplyMissingImage(resolver, binding, imageId, token)
        }
        .also {
          // Register before starting: even an inline completion must be able to remove its record.
          missingImageResolutions[imageId] = MissingImageResolution(token, it)
          it.start()
        }
    }

  private suspend fun supplyMissingImage(
    resolver: MissingImageResolver,
    binding: StyleBinding,
    imageId: String,
    token: Any,
  ) {
    var rememberFailure = false
    try {
      // A queued miss may arrive after another request or style command supplied the image.
      // Consult the engine outside the lifecycle lock: Native marshals this read to its map thread.
      if (binding.imageExists(imageId) == true) return
      val resolved =
        try {
          resolver(imageId)
        } catch (error: CancellationException) {
          throw error
        } catch (error: Throwable) {
          runtime.logger?.w(error) { "The missing-image resolver failed for image '$imageId'" }
          null
        }
      if (resolved == null) {
        rememberFailure = true
        return
      }
      addResolvedStyleImage(binding, imageId, resolved)
    } catch (error: CancellationException) {
      throw error
    } catch (error: Throwable) {
      runtime.logger?.w(error) { "Could not add the resolved image '$imageId'" }
    } finally {
      // Keep negative results to avoid a request loop, but let a later engine miss restore an
      // evicted image. An older resolver must not clear a replacement resolver's pending work.
      if (!rememberFailure) {
        lifecycle.serialized {
          if (missingImageResolutions[imageId]?.token === token)
            missingImageResolutions.remove(imageId)
        }
      }
    }
  }

  /**
   * Adds a resolved image to [binding].
   *
   * The engine asks as soon as it lays out a tile, so this waits out a style command in progress
   * rather than failing on it, and abandons the add once [binding] is no longer the loaded style.
   * The style that it adds to is not ready yet: the browser counts a style as loaded only once
   * every in-view tile has parsed, and a tile does not finish parsing until this add answers it.
   */
  private suspend fun addResolvedStyleImage(
    binding: StyleBinding,
    imageId: String,
    resolved: ResolvedStyleImage,
  ) {
    val record = ImperativeImageRecord(fromResolver = true)
    val reservation = StyleMutationReservation()
    var claimed = false
    while (true) {
      val inProgress = lifecycle.serialized {
        if (lifecycle.isClosed) return
        if (style.currentLoadedStyle() !== binding) return
        (activeStyleMutation ?: backgroundStyleMutation)?.let {
          return@serialized it
        }
        if (hasDesiredImage(imageId) || imperativeImages[imageId]?.fromResolver == false) return
        // Resolver ownership survives eviction, but must not replace an explicitly added image.
        if (imageId !in imperativeImages) {
          imperativeImages[imageId] = record
          claimed = true
        }
        backgroundStyleMutation = reservation
        null
      }
      if (inProgress == null) break
      inProgress.completion.await()
    }
    var committed = false
    try {
      if (binding.imageExists(imageId) == true) return
      // An engine eviction ends the previous image identity, even when its ID is reused.
      binding.identity.images.remove(imageId)
      binding.addImage(imageId, resolved.image, resolved.sdf, resolved.stretch)
      committed = lifecycle.serialized {
        !lifecycle.isClosed && style.isCurrentLoadedStyle(binding)
      }
    } catch (error: CancellationException) {
      throw error
    } catch (error: Throwable) {
      // A style load can invalidate [binding] between the check above and these calls, which fails
      // the add for a style that nothing waits on any more.
      if (lifecycle.serialized { !lifecycle.isClosed && style.isCurrentLoadedStyle(binding) }) {
        if (error is StyleMutationException) {
          throw StyleHandleException("Could not add image '$imageId': ${error.message}", error)
        }
        throw error
      }
    } finally {
      lifecycle.serialized {
        if (claimed && !committed && imperativeImages[imageId] === record)
          imperativeImages.remove(imageId)
        completeStyleMutation(reservation)
      }
    }
  }

  /** Ends every resolution in flight: none of them can still reach the style that asked. */
  private fun cancelMissingImageResolutions() {
    missingImageResolutions.values.forEach { it.work.cancel() }
    missingImageResolutions.clear()
  }

  internal fun removeStyleImage(id: String, expectedStyle: StyleBinding, identity: Any): Boolean {
    val reservation = StyleMutationReservation()
    val binding = lifecycle.serialized {
      requireOpenLocked()
      requireStyleHandleLocked(expectedStyle)
      check(expectedStyle.identity.images.isCurrent(id, identity)) {
        "Image '$id' has been removed or replaced"
      }
      requireNoDesiredImage(id)
      requireNoActiveStyleMutation()
      checkNotNull(style.currentLoadedStyle()).also(::requireStyleHandleLocked).also {
        activeStyleMutation = reservation
      }
    }
    try {
      if (binding.imageExists(id) == false) return false
      binding.removeImage(id)
      lifecycle.serialized {
        requireStyleHandleLocked(binding)
        imperativeImages.remove(id)
        binding.identity.images.remove(id)
      }
      return true
    } catch (error: StyleMutationException) {
      throw StyleHandleException("Could not remove image '$id': ${error.message}", error)
    } finally {
      lifecycle.serialized { completeStyleMutation(reservation) }
    }
  }

  private fun refreshSourcesAfterCommand(binding: StyleBinding): Map<String, SourceHandle> {
    while (true) {
      val read = lifecycle.serialized {
        requireStyleHandleLocked(binding)
        StyleResourceRead(binding, styleHandleEpoch, ++styleSourceChangeRevision)
      }
      val sources = style.readSources(binding)
      val committed = lifecycle.serialized {
        requireStyleHandleLocked(binding)
        if (styleSourceChangeRevision != read.sourceChangeRevision) return@serialized false
        style.updateSources(sources)
        true
      }
      if (committed) return sources
    }
  }

  private fun requireNoDesiredSource(id: String) {
    if (desiredStyleRevision.sources.any { it.id == id }) {
      throw StyleHandleException("Source ID '$id' is declared by the style content")
    }
  }

  private fun hasDesiredImage(id: String): Boolean = desiredStyleRevision.images.any { it.id == id }

  private fun requireNoDesiredImage(id: String) {
    if (hasDesiredImage(id)) {
      throw StyleHandleException("Image ID '$id' is declared by the style content")
    }
  }

  private fun requireNoImperativeResourceConflicts(revision: DesiredStyleRevision) {
    revision.sources
      .firstOrNull { it.id in imperativeSources }
      ?.let {
        throw StyleHandleException("Source ID '${it.id}' is owned by an imperative addition")
      }
    revision.images
      .firstOrNull { it.id in imperativeImages }
      ?.let {
        throw StyleHandleException("Image ID '${it.id}' is owned by an imperative addition")
      }
  }

  private fun requireNoActiveStyleMutation() {
    if (activeStyleMutation != null) {
      throw StyleHandleException("Another imperative style resource command is in progress")
    }
  }

  private fun completeStyleMutation(reservation: StyleMutationReservation) {
    if (activeStyleMutation === reservation) activeStyleMutation = null
    if (backgroundStyleMutation === reservation) backgroundStyleMutation = null
    reservation.completion.complete(Unit)
  }

  internal fun <T> runStyleHandleOperation(
    binding: StyleBinding,
    action: () -> T,
  ): T {
    lifecycle.serialized { requireStyleHandleLocked(binding) }
    val result = action()
    lifecycle.serialized { requireStyleHandleLocked(binding) }
    return result
  }

  private fun requireStyleHandleLocked(binding: StyleBinding) {
    requireOpenLocked()
    check(style.loadState == StyleLoadState.Ready && style.isCurrentLoadedStyle(binding)) {
      "Style operation belongs to a stale or unready loaded-style identity"
    }
  }

  /**
   * Publishes the camera and viewport of [adapter] and returns the presentation that holds them. A
   * map with no readable viewport keeps the values it has, so a caller that reacts to a camera
   * event still reaches its presentation.
   */
  internal fun synchronizeCamera(adapter: MapAdapter): MapAttachment? {
    if (!lifecycle.acceptsPresentation(adapter)) return null
    val cameraPosition = adapter.getCameraPosition()
    val viewport = adapter.getViewport()
    return lifecycle.serialized {
      if (!lifecycle.acceptsPresentation(adapter)) return@serialized null
      val current = currentMapAttachment ?: return@serialized null
      if (viewport != null) {
        cameraPositionState = cameraPosition
        current.updateViewport(viewport)
      }
      current
    }
  }

  /**
   * Reacts to one engine event that the lifecycle already accepted, then publishes it to [events].
   * Ignores an [adapter] that this state no longer accepts. Publication follows the reaction, so a
   * collector reads the values that the event produced.
   */
  internal fun onEvent(adapter: MapAdapter, event: MapEvent) {
    val accepted =
      when (event) {
        is MapEvent.CameraMoveStarted ->
          synchronizeCamera(adapter)?.also { it.cameraChangeStarted() } != null
        MapEvent.CameraMoved -> synchronizeCamera(adapter) != null
        is MapEvent.CameraMoveEnded ->
          synchronizeCamera(adapter)?.also { it.cameraChangeEnded() } != null
        is MapEvent.FrameRendered -> lifecycle.acceptsPresentation(adapter)
        MapEvent.StyleLoaded,
        is MapEvent.StyleLoadFailed,
        is MapEvent.SourceDataFailed,
        MapEvent.Idle -> lifecycle.acceptsAdapter(adapter)
      }
    if (accepted) eventsFlow.tryEmit(event)
  }

  /** Reports whether a gesture holds the camera of [adapter]. */
  internal fun setGestureActive(adapter: MapAdapter, active: Boolean) {
    presentedAttachment(adapter)?.setGestureActive(active)
  }

  /** Reports the engagement of the input node over [adapter]. */
  internal fun setEngaged(adapter: MapAdapter, engaged: Boolean) {
    presentedAttachment(adapter)?.setEngaged(engaged)
  }

  /** Ends a camera change that the engine behind [adapter] will never finish. */
  internal fun endCameraChange(adapter: MapAdapter) {
    presentedAttachment(adapter)?.cameraChangeEnded()
  }

  private fun presentedAttachment(adapter: MapAdapter): MapAttachment? = lifecycle.serialized {
    if (!lifecycle.acceptsPresentation(adapter)) return@serialized null
    currentMapAttachment
  }

  internal fun isCurrent(candidate: MapAttachment): Boolean = lifecycle.serialized {
    isCurrentLocked(candidate)
  }

  internal fun <T> withCurrentOrNull(candidate: MapAttachment, block: () -> T): T? {
    if (!isCurrent(candidate)) return null
    val result = block()
    return result.takeIf { isCurrent(candidate) }
  }

  private fun isCurrentLocked(candidate: MapAttachment): Boolean =
    currentMapAttachment === candidate && lifecycle.isCurrent(candidate.token, candidate.adapter)

  private fun requireOpenLocked() {
    check(!lifecycle.isClosed) { "The map state is closed" }
  }

  internal fun commitClosed() {
    styleHandleEpoch++
    cancelMissingImageResolutions()
    style.invalidateLoadedStyle()
    Snapshot.withMutableSnapshot {
      closedState = true
      currentMapAttachment?.invalidate()
      currentMapAttachment = null
      nextMapAttachment.completeExceptionally(
        CancellationException("The map closed while waiting for an attachment")
      )
    }
  }

  internal fun invalidatePresentation(adapter: MapAdapter?) {
    Snapshot.withMutableSnapshot {
      currentMapAttachment?.invalidate()
      currentMapAttachment = null
      prepareForNextAttachment()
      if (adapter?.retainsEngineBetweenPresentations != true) {
        style.loadState = StyleLoadState.Pending
      }
    }
  }

  internal fun invalidateClosedAdapter(adapter: MapAdapter) {
    Snapshot.withMutableSnapshot {
      styleHandleEpoch++
      style.invalidateLoadedStyle()
      style.loadState = StyleLoadState.Pending
      if (currentMapAttachment?.adapter === adapter) {
        currentMapAttachment?.invalidate()
        currentMapAttachment = null
        prepareForNextAttachment()
      }
    }
  }

  internal fun configurePresentationAdapter(adapter: MapAdapter) {
    val configuration = lifecycle.serialized {
      if (!lifecycle.isPendingPublication(adapter)) return
      PresentationConfiguration(
        camera = CameraCommand(adapter, cameraPositionState, cameraCommandRevision),
        baseStyle = BaseStyleCommand(adapter, style.baseStyle, baseStyleCommandRevision),
      )
    }
    applyCameraCommand(configuration.camera)
    if (!lifecycle.isPendingPublication(adapter)) return
    applyBaseStyleCommand(configuration.baseStyle)
  }

  internal fun beginStyleLoadForNewAdapter() {
    styleHandleEpoch++
    style.invalidateLoadedStyle()
    style.loadState = StyleLoadState.Loading
  }

  internal fun seedPresentationViewport(token: MapPresentationToken, adapter: MapAdapter) {
    val viewport = adapter.getViewport() ?: return
    lifecycle.serialized {
      val current = currentMapAttachment ?: return@serialized
      if (current.token != token || current.adapter !== adapter || current.viewport != null) return
      current.updateViewport(viewport)
    }
  }

  private fun applyBaseStyleCommand(initial: BaseStyleCommand) {
    var command = initial
    while (true) {
      if (lifecycle.currentAdapter() !== command.adapter) return
      command.adapter.setBaseStyle(command.value)
      command = lifecycle.serialized {
        if (lifecycle.currentAdapter() !== command.adapter) return
        if (baseStyleCommandRevision == command.revision) return
        BaseStyleCommand(command.adapter, style.baseStyle, baseStyleCommandRevision)
      }
    }
  }

  private fun applyCameraCommand(initial: CameraCommand) {
    var command = initial
    while (true) {
      if (lifecycle.currentAdapter() !== command.adapter) return
      command.adapter.setCameraPosition(command.value, command.guard)
      command = lifecycle.serialized {
        if (lifecycle.currentAdapter() !== command.adapter) return
        if (cameraCommandRevision == command.revision) return
        CameraCommand(command.adapter, cameraPositionState, cameraCommandRevision, command.guard)
      }
    }
  }

  private fun applyAttachmentCameraCommand(
    attachment: MapAttachment,
    initial: CameraCommand,
  ) {
    val guard = CameraCommandGuard {
      isCurrent(attachment) && initial.guard?.isValid() != false
    }
    var command = initial
    while (true) {
      if (!lifecycle.isCurrent(attachment.token, command.adapter)) return
      command.adapter.setCameraPosition(command.value, guard)
      command = lifecycle.serialized {
        if (!lifecycle.isCurrent(attachment.token, command.adapter)) return
        if (cameraCommandRevision == command.revision) return
        CameraCommand(command.adapter, cameraPositionState, cameraCommandRevision, command.guard)
      }
    }
  }

  internal fun commitPresentation(
    token: MapPresentationToken,
    adapter: MapAdapter,
  ) {
    val attachment = MapAttachment(this, token, adapter)
    currentMapAttachment = attachment
    nextMapAttachment.complete(attachment)
  }

  private suspend fun awaitAttachment(): MapAttachment {
    val pending = lifecycle.serialized {
      requireOpenLocked()
      currentMapAttachment?.let {
        return it
      }
      nextMapAttachment
    }
    return pending.await()
  }

  private suspend fun awaitReplacementAttachment(): MapAttachment {
    val pending = lifecycle.serialized {
      if (lifecycle.isClosed) {
        throw CancellationException("The map closed during the operation")
      }
      currentMapAttachment?.let {
        return it
      }
      nextMapAttachment
    }
    return pending.await()
  }

  private fun prepareForNextAttachment() {
    if (nextMapAttachment.isCompleted) nextMapAttachment = CompletableDeferred()
  }

  private inline fun <T> withAttachmentRead(block: (MapAttachment) -> T?): T? {
    val attachment = currentMapAttachment ?: return null
    return try {
      block(attachment)
    } catch (_: MapAttachmentChangedException) {
      null
    }
  }

  private data class PresentationConfiguration(
    val camera: CameraCommand,
    val baseStyle: BaseStyleCommand,
  )

  private data class CameraCommand(
    val adapter: MapAdapter,
    val value: CameraPosition,
    val revision: Long,
    val guard: CameraCommandGuard? = null,
  )

  private data class AttachmentCameraCommand(
    val attachment: MapAttachment,
    val command: CameraCommand,
  )

  private data class BaseStyleCommand(
    val adapter: MapAdapter,
    val value: BaseStyle,
    val revision: Long,
  )

  private data class StyleResourceRead(
    val binding: StyleBinding,
    val styleHandleEpoch: Long,
    val sourceChangeRevision: Long,
  )
}

@JvmInline internal value class MapPresentationToken(val value: Long)

internal class MapPresentationOwnerToken

/**
 * Remembers a logical map and closes it when this call leaves composition.
 *
 * [baseStyle] owns the map's base style and updates it on recomposition. Its
 * [MapStyleState.asMutable] is null. [initialCameraPosition] only seeds the camera; use
 * [MapState.setCameraPosition] to move it. Changes to [content] update the declared resources.
 * Restoration creates a new map with the saved camera position and the current [baseStyle].
 *
 * [content] declares the map's sources, layers, and images. It reads the returned state through
 * [LocalMapState] and its viewport through [LocalViewport].
 */
@Composable
public fun rememberMapState(
  runtime: MapRuntime = DefaultMapRuntime.instance,
  baseStyle: BaseStyle = BaseStyle.Demo,
  initialCameraPosition: CameraPosition = CameraPosition(),
  content: @Composable @MaplibreComposable () -> Unit = {},
): MapState {
  val currentContent by rememberUpdatedState(content)
  val stableContent = remember<@Composable @MaplibreComposable () -> Unit> { { currentContent() } }
  val state =
    rememberSaveable(runtime, saver = mapStateSaver(runtime, baseStyle, stableContent)) {
      runtime
        .createMapState(
          baseStyle = baseStyle,
          cameraPosition = initialCameraPosition,
          content = stableContent,
        )
        .also { it.style.baseStyleDeclared = true }
    }
  SideEffect { state.style.updateBaseStyle(baseStyle) }
  DisposableEffect(state) { onDispose { state.close() } }
  return state
}

private fun mapStateSaver(
  runtime: MapRuntime,
  baseStyle: BaseStyle,
  content: @Composable @MaplibreComposable () -> Unit,
): Saver<MapState, List<Double>> =
  Saver(
    save = { state ->
      with(state.cameraPosition) {
        listOf(bearing, target.longitude, target.latitude, tilt, zoom)
      }
    },
    restore = { values ->
      require(values.size == 5) { "A saved camera position must contain five values" }
      runtime
        .createMapState(
          baseStyle = baseStyle,
          cameraPosition =
            CameraPosition(
              bearing = values[0],
              target = Position(longitude = values[1], latitude = values[2]),
              tilt = values[3],
              zoom = values[4],
            ),
          content = content,
        )
        .also { it.style.baseStyleDeclared = true }
    },
  )

internal class RuntimeImplementation(
  internal val platformContext: Any?,
  private val closeResources: suspend () -> Unit,
  internal val logger: MapLog?,
  offlineManagerBackend: OfflineManager = UnsupportedOfflineManager,
  internal val physicalScope: CoroutineScope =
    CoroutineScope(SupervisorJob() + Dispatchers.Default),
  internal val createSnapshotterAdapter: () -> SnapshotterAdapter = ::unsupportedSnapshots,
  internal val styleEvaluator: StyleCompositionEvaluator = DefaultStyleCompositionEvaluator,
  internal val resourceConfig: MapResourceConfig = MapResourceConfig(),
) : MapRuntime {
  override val offlineManager: OfflineManager =
    RuntimeBoundOfflineManager(
      delegate = offlineManagerBackend,
      requireRuntimeOpen = ::requireOpen,
    )
  private val lock = reentrantLock()
  private val children = linkedSetOf<MapState>()
  private val snapshotters = linkedSetOf<MapSnapshotterImplementation>()
  private val closure = CompletableDeferred<Result<Unit>>()
  private var closed = false
  private var closedState: Boolean by mutableStateOf(false)

  final override fun createMapState(
    baseStyle: BaseStyle,
    cameraPosition: CameraPosition,
    content: @Composable @MaplibreComposable () -> Unit,
  ): MapState = lock.withLock {
    requireOpenLocked()
    MapState(this, cameraPosition, baseStyle, content).also(children::add)
  }

  final override fun createSnapshotter(
    baseStyle: BaseStyle,
    content: @Composable @MaplibreComposable () -> Unit,
  ): MapSnapshotter = lock.withLock {
    requireOpenLocked()
    MapSnapshotterImplementation(this, baseStyle, content).also(snapshotters::add)
  }

  private fun requireOpen() {
    lock.withLock { requireOpenLocked() }
  }

  private fun requireOpenLocked() {
    check(!closed) { "The map runtime is closed" }
  }

  override fun close() {
    val closingChildren = lock.withLock {
      if (closed) return
      closed = true
      Snapshot.withMutableSnapshot { closedState = true }
      children.toList() to snapshotters.toList()
    }
    val (closingStates, closingSnapshotters) = closingChildren
    closingStates.forEach(MapState::close)
    closingSnapshotters.forEach(MapSnapshotterImplementation::close)
    physicalScope.launch(start = CoroutineStart.UNDISPATCHED) {
      val failures = mutableListOf<Throwable>()
      closingStates.forEach { child ->
        runCatching { child.awaitClosed() }.exceptionOrNull()?.let(failures::addCleanupFailure)
      }
      closingSnapshotters.forEach { child ->
        runCatching { child.awaitClosed() }.exceptionOrNull()?.let(failures::addCleanupFailure)
      }
      runCatching { closeResources() }.exceptionOrNull()?.let(failures::addCleanupFailure)
      closure.complete(failures.cleanupResult("Map runtime"))
    }
  }

  override val isClosed: Boolean
    get() = closedState

  override suspend fun awaitClosed() {
    closure.await().getOrThrow()
  }

  internal fun childClosed(child: MapState) {
    lock.withLock { children.remove(child) }
  }

  internal fun childClosed(child: MapSnapshotterImplementation) {
    lock.withLock { snapshotters.remove(child) }
  }
}
